import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../services/analyticsService';
import { createBlankRecipe } from '../../services/recipeDraftService';
import { PlanLimitDialog } from '../Billing/PlanLimitPrompt';
import { BillingLimit, billingLimitFromError } from '../../utils/billingLimit';

type StarterRecipeId = 'weather' | 'file' | 'bitcoin';

interface StarterRecipe {
  id: StarterRecipeId;
  title: string;
  description: string;
  setupLabel: string;
  content: string;
  icon: React.ReactNode;
  iconClassName: string;
}

const STARTER_RECIPES: StarterRecipe[] = [
  {
    id: 'weather',
    title: 'Tomorrow’s Outfit',
    description: 'A friendly daily helper for deciding what to wear.',
    setupLabel: '1-minute setup',
    content: 'Every evening at 9 PM, check tomorrow’s local weather. Suggest what I should wear and alert me if I need an umbrella for work.',
    iconClassName: 'bg-sky-100 text-sky-700',
    icon: (
      <svg className="h-7 w-7" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M10 23.5h13a5 5 0 0 0 .2-10 7.5 7.5 0 0 0-14.3-1.8A6 6 0 0 0 10 23.5Z" fill="currentColor" opacity=".22" />
        <path d="M10 23.5h13a5 5 0 0 0 .2-10 7.5 7.5 0 0 0-14.3-1.8A6 6 0 0 0 10 23.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m12 27-1 2m6-2-1 2m6-2-1 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'file',
    title: 'New File Assistant',
    description: 'A simple way to react whenever a file arrives.',
    setupLabel: 'Connects a folder',
    content: 'Whenever a new file is placed in this folder, email me its name and a short summary of what it contains.',
    iconClassName: 'bg-emerald-50 text-emerald-700',
    icon: (
      <svg className="h-7 w-7" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M7 9.5h7l2 2h9v12.75A2.75 2.75 0 0 1 22.25 27H9.75A2.75 2.75 0 0 1 7 24.25V9.5Z" fill="currentColor" opacity=".12" strokeLinejoin="round" />
        <path d="M7 12h18" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'bitcoin',
    title: 'Paper Bitcoin Trader',
    description: 'Practice a trading idea safely with imaginary money.',
    setupLabel: 'No real money',
    content: 'Start with $100,000 in imaginary funds. Buy 1 Bitcoin below $60,000 and sell it above $100,000. Telegram me after each simulated trade.',
    iconClassName: 'bg-amber-100 text-amber-700',
    icon: <span className="text-2xl font-semibold" aria-hidden="true">₿</span>,
  },
];

const initialContent = STARTER_RECIPES.reduce<Record<StarterRecipeId, string>>((contents, recipe) => {
  contents[recipe.id] = recipe.content;
  return contents;
}, { weather: '', file: '', bitcoin: '' });

const StarterRecipeHome: React.FC<{ isYolo: boolean }> = ({ isYolo }) => {
  const navigate = useNavigate();
  const [contents, setContents] = useState(initialContent);
  const [creatingRecipe, setCreatingRecipe] = useState<StarterRecipeId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [planLimit, setPlanLimit] = useState<BillingLimit | null>(null);

  const createStarterRecipe = async (recipe: StarterRecipe) => {
    const description = contents[recipe.id].trim();
    if (!description || creatingRecipe) return;

    setCreatingRecipe(recipe.id);
    setError(null);
    try {
      const createdRecipe = await createBlankRecipe({
        name: recipe.title,
        content: description,
        triggerType: 'manual',
        ensureUniqueName: true,
      });
      trackEvent('recipe_created', {
        recipe_id: createdRecipe.id,
        content_length: description.length,
        creation_flow: 'starter_recipe',
        starter_recipe: recipe.id,
      });
      navigate(`/recipes/${createdRecipe.id}/edit`);
    } catch (reason: any) {
      setCreatingRecipe(null);
      const nextPlanLimit = billingLimitFromError(reason);
      if (nextPlanLimit) {
        setPlanLimit(nextPlanLimit);
        return;
      }
      setError(reason?.response?.data?.message || reason?.message || 'Could not create this recipe');
    }
  };

  return (
    <main className={`min-w-0 flex-1 overflow-y-auto ${isYolo ? 'bg-[#f7f3e8]' : 'bg-gray-50'}`}>
      {planLimit && <PlanLimitDialog limit={planLimit} onClose={() => setPlanLimit(null)} />}
      <div className="mx-auto flex min-h-full w-full max-w-6xl items-center px-5 py-8 sm:px-7 lg:px-10 lg:py-10">
        <section className="w-full" aria-labelledby="starter-recipes-title">
          <header className="mx-auto mb-7 max-w-3xl text-center lg:mb-9">
            <h1 id="starter-recipes-title" className={`text-3xl font-semibold tracking-tight sm:text-4xl ${isYolo ? 'text-stone-900' : 'text-gray-950'}`}>
              Try out one of these automation recipes below.
            </h1>
            <p className={`mx-auto mt-3 max-w-2xl text-sm leading-6 sm:text-base ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>
              A recipe is simply a set of instructions you can edit, try, and run.
            </p>
          </header>

          <div className="grid gap-4 md:grid-cols-3 lg:gap-5">
            {STARTER_RECIPES.map(recipe => {
              const isCreating = creatingRecipe === recipe.id;
              return (
                <article
                  key={recipe.id}
                  className={`flex min-h-[21rem] flex-col rounded-2xl border p-5 shadow-[0_12px_32px_rgba(69,54,29,0.07)] transition hover:-translate-y-0.5 hover:shadow-[0_18px_38px_rgba(69,54,29,0.11)] ${isYolo ? 'border-[#e7dac4] bg-[#fffaf1]' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${recipe.iconClassName}`}>
                      {recipe.icon}
                    </div>
                    <span className={`rounded-full px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide ${isYolo ? 'bg-[#f2eadc] text-stone-500' : 'bg-gray-100 text-gray-500'}`}>
                      {recipe.setupLabel}
                    </span>
                  </div>

                  <h2 className={`mt-4 text-xl font-semibold tracking-tight ${isYolo ? 'text-stone-900' : 'text-gray-950'}`}>{recipe.title}</h2>
                  <p className={`mt-1 text-sm leading-5 ${isYolo ? 'text-stone-600' : 'text-gray-600'}`}>{recipe.description}</p>

                  <div className={`mt-4 flex min-h-[8.25rem] flex-1 flex-col overflow-hidden rounded-xl border ${isYolo ? 'border-[#ded2bf] bg-white' : 'border-gray-200 bg-white'}`}>
                    <div className={`flex items-center justify-between border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wider ${isYolo ? 'border-[#eee6d9] bg-[#fbf8f2] text-stone-500' : 'border-gray-100 bg-gray-50 text-gray-500'}`}>
                      <span>Recipe</span>
                      <span className={`normal-case tracking-normal ${isYolo ? 'text-amber-700' : 'text-blue-600'}`}>Editable</span>
                    </div>
                    <textarea
                      value={contents[recipe.id]}
                      onChange={event => setContents(current => ({ ...current, [recipe.id]: event.target.value }))}
                      disabled={Boolean(creatingRecipe)}
                      aria-label={`${recipe.title} recipe instructions`}
                      className="min-h-[6.5rem] w-full flex-1 resize-none border-0 bg-white px-3 py-2.5 text-xs leading-5 text-stone-700 outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 disabled:bg-gray-50"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => void createStarterRecipe(recipe)}
                    disabled={!contents[recipe.id].trim() || Boolean(creatingRecipe)}
                    className={`mt-4 inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 ${isYolo ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500 focus:ring-offset-[#fffaf1]' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'}`}
                  >
                    {isCreating ? 'Creating recipe…' : 'Try this recipe'}
                  </button>
                </article>
              );
            })}
          </div>

          {error && <p className="mt-4 text-center text-sm text-red-600" role="alert">{error}</p>}

          <div className={`mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm ${isYolo ? 'text-stone-500' : 'text-gray-500'}`}>
            <span>Already know what you want?</span>
            <button
              type="button"
              onClick={() => navigate('/recipes/new')}
              className={`font-semibold underline decoration-1 underline-offset-4 ${isYolo ? 'text-amber-800 hover:text-amber-950' : 'text-blue-700 hover:text-blue-900'}`}
            >
              Create your own recipe
            </button>
          </div>
        </section>
      </div>
    </main>
  );
};

export default StarterRecipeHome;
